import { useState, useEffect } from "react";
import { DateYr } from "../lib/shortDate";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { F, th as palette, Btn } from "@/lib/bricks";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Page, PageHeader, Toolbar, Search, SmallSelect, List, Row, Empty, TextLink, Loading, FieldLabel } from "@/components/calm/Calm";

function fmtSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function DocumentsPage() {
  const { resolvedTheme } = useTheme();
  const { session } = useAuth();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [search, setSearch] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [uploadForm, setUploadForm] = useState({ name: '', folder_id: '', tags: '' });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [folderName, setFolderName] = useState('');

  const BASE = process.env.REACT_APP_BACKEND_URL;
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };

  const load = () => {
    fetch(`${BASE}/api/documents/folders`, { headers }).then(r => r.json()).then(d => { setFolders(Array.isArray(d) ? d : []); setLoading(false); }).catch(() => setLoading(false));
    fetch(`${BASE}/api/documents`, { headers }).then(r => r.json()).then(d => setDocuments(Array.isArray(d) ? d : [])).catch(() => {});
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleCreateFolder = async () => {
    if (!folderName) return toast.error("Folder name required");
    try {
      await fetch(`${BASE}/api/documents/folders`, { method: 'POST', headers, body: JSON.stringify({ name: folderName, parent_id: selectedFolder }) });
      toast.success("Folder created");
      setShowNewFolder(false);
      setFolderName('');
      load();
    } catch { toast.error("Failed to create folder"); }
  };

  const handleUpload = async () => {
    if (!uploadFile) return toast.error("Choose a file to upload");
    const displayName = (uploadForm.name || uploadFile.name || '').trim();
    if (!displayName) return toast.error("Document name required");
    setUploading(true);
    try {
      const res = await fetch(`${BASE}/api/documents/upload-url`, {
        method: 'POST', headers,
        body: JSON.stringify({
          filename: uploadFile.name,
          content_type: uploadFile.type || 'application/octet-stream',
          file_size: uploadFile.size,
          folder_id: uploadForm.folder_id || null,
          tags: uploadForm.tags.split(',').map(t => t.trim()).filter(Boolean),
          name: displayName,
          for_esign: false,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(typeof body.detail === 'string' ? body.detail : 'Failed to prepare upload');
      }
      const { upload_url } = await res.json();
      const putRes = await fetch(upload_url, {
        method: 'PUT',
        body: uploadFile,
        headers: { 'Content-Type': uploadFile.type || 'application/octet-stream' },
      });
      if (!putRes.ok) throw new Error('Failed to upload file bytes');
      toast.success("Document uploaded");
      setShowUpload(false);
      setUploadForm({ name: '', folder_id: '', tags: '' });
      setUploadFile(null);
      load();
    } catch (err) {
      toast.error(err.message || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const filteredDocs = documents.filter(d => {
    const folderMatch = selectedFolder ? d.folder_id === selectedFolder : true;
    if (!search) return folderMatch;
    const q = search.toLowerCase();
    return folderMatch && (d.name?.toLowerCase().includes(q) || (d.tags || []).some(t => t.toLowerCase().includes(q)));
  });

  // Folder picker: nested folders are indented in the select.
  const folderOptions = [];
  const walk = (parentId, depth) => folders.filter(f => (f.parent_id || null) === parentId).forEach(f => {
    folderOptions.push({ key: f.id, label: `${'\u00A0\u00A0'.repeat(depth)}${f.name} (${documents.filter(d => d.folder_id === f.id).length})` });
    walk(f.id, depth + 1);
  });
  walk(null, 0);

  if (loading) return <Loading />;

  const openUpload = () => { setUploadFile(null); setUploadForm({ name: '', folder_id: selectedFolder || '', tags: '' }); setShowUpload(true); };
  const folderOf = (id) => folders.find(f => f.id === id)?.name;

  return (
    <Page testid="documents-page" width={960}>
      <PageHeader title="Documents"
        subtitle={`${documents.length} files, ${folders.length} folders`}
        action={<>
          <Btn variant="ghost" dark={dark} onClick={() => setShowNewFolder(true)}>New folder</Btn>
          <Btn variant="primary" dark={dark} onClick={openUpload}>Upload</Btn>
        </>} />

      <Toolbar>
        <Search value={search} onChange={setSearch} placeholder="Search files and tags" testid="documents-search" />
        <SmallSelect label="Folder" value={selectedFolder || ''} onChange={(v) => setSelectedFolder(v || null)}
          options={[{ key: '', label: 'All folders' }, ...folderOptions]} testid="documents-folder" />
      </Toolbar>

      {filteredDocs.length === 0 ? (
        <Empty action={search ? null : <TextLink onClick={openUpload}>Upload a document</TextLink>}>
          {search ? 'No documents match your search.' : selectedFolder ? 'No documents in this folder.' : 'No documents yet.'}
        </Empty>
      ) : (
        <List testid="documents-list">
          {filteredDocs.map(doc => (
            <Row key={doc.id} testid={`document-${doc.id}`}
              title={doc.name}
              meta={[{ text: fmtSize(doc.size), figure: true }, { text: <DateYr d={doc.created_at} />, figure: true }, !selectedFolder && folderOf(doc.folder_id), (doc.tags || []).join(', ')]}
              next={doc.transaction_address ? `Linked to ${doc.transaction_address}` : null}
              aside={doc.file_url ? (
                <a href={doc.file_url} target="_blank" rel="noopener noreferrer" download
                  style={{ fontSize: 13, color: p.ink2, textDecoration: 'underline', textUnderlineOffset: 3, textDecorationColor: p.hairS }}>Download</a>
              ) : null} />
          ))}
        </List>
      )}

      {/* New Folder Modal */}
      <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
        <DialogContent style={{ maxWidth: 360, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>New folder</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              {selectedFolder ? `Create a subfolder inside "${folders.find(f => f.id === selectedFolder)?.name}"` : 'Create a new top-level folder.'}
            </DialogDescription>
          </DialogHeader>
          <div style={{ paddingTop: 20 }}>
            <FieldLabel>Folder name</FieldLabel>
            <input type="text" value={folderName} onChange={e => setFolderName(e.target.value)} placeholder="e.g. Completed deals"
              style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowNewFolder(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleCreateFolder}>Create folder</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload modal: real file upload via /api/documents/upload-url */}
      <Dialog open={showUpload} onOpenChange={(open) => { setShowUpload(open); if (!open) { setUploadFile(null); setUploading(false); } }}>
        <DialogContent style={{ maxWidth: 460, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Upload a document</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>Upload a file to your brokerage document store.</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
            <div><FieldLabel>File</FieldLabel>
              <input type="file" id="docs-file-upload" style={{ marginTop: 8, width: '100%', fontSize: 13, fontFamily: F.display, color: p.ink }}
                onChange={e => {
                  const f = e.target.files?.[0] || null;
                  setUploadFile(f);
                  if (f && !uploadForm.name) setUploadForm({ ...uploadForm, name: f.name.replace(/\.[^.]+$/, '') });
                }}/>
              {uploadFile && <div style={{ marginTop: 6, fontSize: 12, color: p.ink3 }}>{uploadFile.name} · {(uploadFile.size / 1024).toFixed(1)} KB</div>}
            </div>
            <div><FieldLabel>Document name</FieldLabel>
              <input type="text" value={uploadForm.name} onChange={e => setUploadForm({ ...uploadForm, name: e.target.value })} placeholder="e.g. Signed purchase agreement"
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
            </div>
            <div><FieldLabel>Folder</FieldLabel>
              <select value={uploadForm.folder_id} onChange={e => setUploadForm({ ...uploadForm, folder_id: e.target.value })}
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                <option value="">No folder</option>
                {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div><FieldLabel>Tags, separated by commas</FieldLabel>
              <input type="text" value={uploadForm.tags} onChange={e => setUploadForm({ ...uploadForm, tags: e.target.value })} placeholder="e.g. signed, urgent"
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
            </div>
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowUpload(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleUpload} disabled={uploading}>{uploading ? 'Uploading…' : 'Upload'}</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
